// SPDX-FileCopyrightText: 2025-2026 Social Connect Labs, Inc.
// SPDX-License-Identifier: BUSL-1.1
// NOTE: Converts to Apache-2.0 on 2029-06-11 per LICENSE.

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { ProofGenerationStep } from '@selfxyz/euclid';
import { ProofProgressScreen, SelfLogo } from '@selfxyz/euclid';
import type { ProvingStateType } from '@selfxyz/mobile-sdk-alpha/browser';
import { useProvingStore } from '@selfxyz/mobile-sdk-alpha/browser';

import { useSelfClient } from '../../providers/SelfClientProvider';
import { useVerificationRequest } from '../../providers/VerificationRequestProvider';
import { WEB_SAFE_AREA } from '../../utils/insets';
import { isDemoMode } from '../../utils/mockOnboardingFlow';
import { initSelfAppFromRequest } from '../../utils/selfAppContext';

const DEMO_DISCLOSE_STEPS: ProofGenerationStep[] = [
  'readingRegistry',
  'generatingProof',
  'awaitingVerification',
  'finishingUp',
];

const DemoEmbedDiscloseScreen: React.FC<{ search: string }> = ({ search }) => {
  const navigate = useNavigate();
  const { appName, displayAppEndpoint, timestamp } = useVerificationRequest();
  const [stepIndex, setStepIndex] = useState(0);

  const advance = useCallback(() => {
    if (stepIndex < DEMO_DISCLOSE_STEPS.length - 1) {
      setStepIndex(i => i + 1);
    } else {
      navigate(`/disclose/result${search}`, { replace: true, state: { success: true } });
    }
  }, [stepIndex, navigate, search]);

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <ProofProgressScreen
        {...WEB_SAFE_AREA}
        appIcon={<SelfLogo size={40} />}
        appName={appName}
        appEndpoint={displayAppEndpoint}
        documentType="passport"
        timestamp={timestamp}
        step={DEMO_DISCLOSE_STEPS[stepIndex]}
      />
      <div onClick={advance} style={{ position: 'absolute', inset: 0, zIndex: 1, cursor: 'pointer' }} />
    </div>
  );
};

const MAX_DISCLOSE_RETRIES = 3;
const DISCLOSE_RETRY_DELAY_MS = 3000;
const ERROR_STATES: ProvingStateType[] = ['error', 'failure', 'passport_not_supported', 'passport_data_not_found'];

function mapDiscloseStateToStep(state: ProvingStateType | null): ProofGenerationStep {
  if (!state) return 'readingRegistry';

  switch (state) {
    case 'idle':
    case 'parsing_id_document':
    case 'fetching_data':
    case 'validating_document':
      return 'readingRegistry';
    case 'init_tee_connexion':
    case 'ready_to_prove':
    case 'proving':
      return 'generatingProof';
    case 'post_proving':
      return 'awaitingVerification';
    case 'completed':
      return 'finishingUp';
    default:
      return 'readingRegistry';
  }
}

const StandardEmbedDiscloseScreen: React.FC = () => {
  const navigate = useNavigate();
  const { client, analytics, haptic } = useSelfClient();
  const verificationCtx = useVerificationRequest();
  const { appName, displayAppEndpoint, timestamp } = verificationCtx;
  const currentState = useProvingStore(s => s.currentState);
  const init = useProvingStore(s => s.init);
  const errorCode = useProvingStore(s => s.error_code);
  const reason = useProvingStore(s => s.reason);

  const startedRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [initDone, setInitDone] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  const navigateToError = useCallback(
    (error: string) => {
      haptic.trigger('error');
      navigate('/disclose/result', {
        replace: true,
        state: { success: false, error, source: 'disclose' as const },
      });
    },
    [haptic, navigate],
  );

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const start = async () => {
      initSelfAppFromRequest(client, verificationCtx);
      analytics.trackEvent('tunnel_disclose_started');
      await init(client, 'disclose', true);
      setInitDone(true);
    };
    void start().catch(err => {
      const message = err instanceof Error ? err.message : 'Disclose init failed';
      analytics.trackEvent('tunnel_disclose_init_failed', { error: message });
      navigateToError(message);
    });
  }, [client, init, analytics, verificationCtx, navigateToError]);

  useEffect(() => {
    if (!currentState || hasCompleted || !initDone) return;

    const isError = ERROR_STATES.includes(currentState);

    if (isError && currentState === 'passport_data_not_found' && retryCountRef.current < MAX_DISCLOSE_RETRIES) {
      retryCountRef.current += 1;
      analytics.trackEvent('tunnel_disclose_retry', { attempt: retryCountRef.current });
      retryTimeoutRef.current = setTimeout(() => {
        const retry = async () => {
          initSelfAppFromRequest(client, verificationCtx);
          await init(client, 'disclose', true);
        };
        void retry().catch(err => {
          const message = err instanceof Error ? err.message : 'Disclose retry failed';
          analytics.trackEvent('tunnel_disclose_retry_failed', { error: message });
          navigateToError(message);
        });
      }, DISCLOSE_RETRY_DELAY_MS);
    } else if (isError) {
      analytics.trackEvent('tunnel_disclose_failed', {
        errorCode,
        reason,
        state: currentState,
      });
      navigateToError(reason ?? errorCode ?? currentState);
    } else if (currentState === 'completed') {
      setHasCompleted(true);
      analytics.trackEvent('tunnel_proving_disclose_complete');
      haptic.trigger('success');
      navigate('/disclose/result', { replace: true, state: { success: true } });
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [
    currentState,
    hasCompleted,
    initDone,
    client,
    init,
    analytics,
    haptic,
    navigate,
    verificationCtx,
    errorCode,
    reason,
    navigateToError,
  ]);

  return (
    <ProofProgressScreen
      {...WEB_SAFE_AREA}
      appIcon={<SelfLogo size={40} />}
      appName={appName}
      appEndpoint={displayAppEndpoint}
      documentType="passport"
      timestamp={timestamp}
      step={mapDiscloseStateToStep(currentState)}
    />
  );
};

export const EmbedDiscloseScreen: React.FC = () => {
  const location = useLocation();

  if (isDemoMode(location.search)) {
    return <DemoEmbedDiscloseScreen search={location.search} />;
  }

  return <StandardEmbedDiscloseScreen />;
};
